// Переходы между экранами при свайпе (этап 10): экран идёт за пальцем (dragTo), при
// отпускании доезжает (slideOut) или возвращается (snapBack), а следующий экран въезжает
// с нужной стороны (pageEntrance). Роутер у нас hash-based и держит один экран, поэтому
// «въезд» передаётся через sessionStorage: уходящий экран записывает, откуда войти
// следующему, тот читает запись при монтировании и проигрывает keyframe из global.css.
//
// Без движения там, где движение не нужно: при «Уменьшить движение» в системе, в
// jsdom (нет размеров) и при нулевой ширине экрана переходы мгновенные — навигация не
// зависит от анимации.

export const ENTRANCE_KEY = 'petrovich:page-entrance';
export const DRAGGING_CLASS = 'is-dragging';
const ENTER_CLASSES = { left: 'page-enter-from-left', right: 'page-enter-from-right' };
const SLIDE_MS = 260;
const SNAP_MS = 320;
// Выезд — «ease-out», возврат — пружинка с лёгким перелётом (как у системных жестов).
const SLIDE_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const SNAP_EASING = 'cubic-bezier(0.34, 1.4, 0.64, 1)';

export function prefersReducedMotion() {
  try {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  } catch {
    return false;
  }
}

function widthOf(node) {
  return node?.getBoundingClientRect?.().width || node?.offsetWidth || 0;
}

export function shouldAnimate(node) {
  return Boolean(node) && widthOf(node) > 0 && !prefersReducedMotion();
}

function afterTransition(node, duration) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) {
        return;
      }
      done = true;
      node.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (event) => {
      if (!event || event.target === node) {
        finish();
      }
    };
    node.addEventListener('transitionend', onEnd);
    // transitionend может не прийти (вкладка в фоне, jsdom) — страховочный таймер.
    setTimeout(finish, duration + 60);
  });
}

function clearInline(node) {
  node.style.transition = '';
  node.style.transform = '';
  node.classList.remove(DRAGGING_CLASS);
}

/** Экран следует за пальцем: без transition, только transform. */
export function dragTo(node, dx) {
  if (!node) {
    return;
  }
  node.classList.add(DRAGGING_CLASS);
  node.style.transition = 'none';
  node.style.transform = `translateX(${Math.round(dx)}px)`;
}

/** Палец отпущен без зачёта: экран возвращается на место с пружинкой. */
export async function snapBack(node) {
  if (!node) {
    return;
  }
  if (!shouldAnimate(node)) {
    clearInline(node);
    return;
  }
  node.style.transition = `transform ${SNAP_MS}ms ${SNAP_EASING}`;
  node.style.transform = 'translateX(0)';
  await afterTransition(node, SNAP_MS);
  clearInline(node);
}

/** Экран уезжает за край (direction — куда уезжает: 'left' | 'right'). */
export async function slideOut(node, direction) {
  if (!node || !shouldAnimate(node)) {
    return;
  }
  node.classList.add(DRAGGING_CLASS);
  node.style.transition = `transform ${SLIDE_MS}ms ${SLIDE_EASING}`;
  node.style.transform = `translateX(${direction === 'left' ? '-100%' : '100%'})`;
  await afterTransition(node, SLIDE_MS);
}

export function setEntrance(from) {
  try {
    if (from === 'left' || from === 'right') {
      sessionStorage.setItem(ENTRANCE_KEY, from);
    } else {
      sessionStorage.removeItem(ENTRANCE_KEY);
    }
  } catch {
    // приватный режим / запрет storage — без анимации входа, навигация не страдает
  }
}

export function takeEntrance() {
  try {
    const value = sessionStorage.getItem(ENTRANCE_KEY);
    sessionStorage.removeItem(ENTRANCE_KEY);
    return value === 'left' || value === 'right' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Уехать в сторону direction и перейти на hash; следующий экран въедет с
 * противоположной стороны. Без анимации (jsdom, reduced motion) — сразу.
 */
export function navigateWithSlide(node, direction, hash) {
  const enterFrom = direction === 'left' ? 'right' : 'left';
  if (!shouldAnimate(node)) {
    setEntrance(enterFrom);
    window.location.hash = hash;
    return Promise.resolve();
  }
  return slideOut(node, direction).then(() => {
    setEntrance(enterFrom);
    window.location.hash = hash;
  });
}

/** Svelte-action: проиграть въезд экрана, если предыдущий экран его заказал. */
export function pageEntrance(node) {
  const from = takeEntrance();
  if (!from || !shouldAnimate(node)) {
    return { destroy() {} };
  }
  const className = ENTER_CLASSES[from];
  const onEnd = (event) => {
    if (!event || event.target === node) {
      node.classList.remove(className);
      node.removeEventListener('animationend', onEnd);
    }
  };
  node.addEventListener('animationend', onEnd);
  node.classList.add(className);
  const guard = setTimeout(() => onEnd(null), SLIDE_MS + 200);
  return {
    destroy() {
      clearTimeout(guard);
      node.removeEventListener('animationend', onEnd);
      node.classList.remove(className);
    },
  };
}
